import React from 'react';
import { Modal, Alert, Button, Tag, Space, Typography, Spin, Divider } from 'antd';
import { CalendarOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import {
  useGetActiveDailyMenusQuery,
  useGetDailyMenuCountsQuery,
  useGetDailyMenuCountsRangeQuery,
} from '../../services/dailyMenuApi';
import type { DailyMenu, DailyMenuCourse } from '../../types/dailyMenu';
import { MEAL_TYPE_LABELS, MEAL_TYPE_COLORS } from '../dailyMenu/mealTypeConstants';
import './dashboardStrip.css';

const { Text, Title } = Typography;

/**
 * DailyMenu.Date is a date-only value stored at midnight UTC (it *is* the
 * business date, already resolved in the tenant tz by the backend), so slicing
 * the ISO string is safe — there's no time component that could shift the day.
 */
function toBusinessDate(iso: string): string {
  return iso.slice(0, 10);
}

const renderCourseWithCounts = (
  course: DailyMenuCourse,
  optionCounts: Map<string, number>,
  fixedCourseCounts: Map<string, number>,
  currencySymbol: string,
) => {
  if (!course.isSelectable) {
    const count = fixedCourseCounts.get(course.id) ?? 0;
    return (
      <div key={course.id} style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ fontSize: 13, color: 'var(--color-neutral-text)', display: 'block', marginBottom: 4 }}>
          {course.name}
        </Text>
        {course.fixedDescription && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {course.fixedDescription}
            <span className="dash-count" style={{ fontSize: 12, marginLeft: 6 }}>
              — {count}
            </span>
          </Text>
        )}
      </div>
    );
  }

  const availableOptions = course.options.filter(o => o.isAvailable);
  const unavailableOptions = course.options.filter(o => !o.isAvailable);

  return (
    <div key={course.id} style={{ flex: 1, minWidth: 0 }}>
      <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
        {course.name}
      </Text>
      <div>
        {availableOptions.map(option => {
          const count = optionCounts.get(option.id) ?? 0;
          return (
            <div key={option.id} style={{ lineHeight: '22px' }}>
              <Text style={{ fontSize: 13 }}>{option.name}</Text>
              {option.priceModifier > 0 && (
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                  (+{currencySymbol}{option.priceModifier.toFixed(2)})
                </Text>
              )}
              <span
                className={count > 0 ? 'dash-count' : 'dash-count dash-count--zero'}
                style={{ fontSize: 13, marginLeft: 6 }}
              >
                — {count}
              </span>
            </div>
          );
        })}
        {unavailableOptions.map(option => (
          <div key={option.id} style={{ lineHeight: '22px' }}>
            <Text delete type="secondary" style={{ fontSize: 13 }}>
              <StopOutlined style={{ fontSize: 10, marginRight: 4 }} />
              {option.name}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};

interface MenuRowProps {
  menu: DailyMenu;
  isMobile: boolean;
  currencySymbol: string;
}

const MenuRow: React.FC<MenuRowProps> = ({ menu, isMobile, currencySymbol }) => {
  const { data: counts } = useGetDailyMenuCountsQuery(menu.id);

  const optionCounts = React.useMemo(() => {
    const map = new Map<string, number>();
    counts?.courses.forEach(c =>
      c.options.forEach(o => {
        if (o.optionId) map.set(o.optionId, o.count);
      }),
    );
    return map;
  }, [counts]);

  const fixedCourseCounts = React.useMemo(() => {
    const map = new Map<string, number>();
    counts?.courses
      .filter(c => !c.isSelectable)
      .forEach(c => map.set(c.courseId, c.totalCount));
    return map;
  }, [counts]);

  const sortedCourses = [...menu.courses].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Space>
          <Title level={5} style={{ margin: 0 }}>{menu.name}</Title>
          <Tag color={MEAL_TYPE_COLORS[menu.mealType]}>
            {MEAL_TYPE_LABELS[menu.mealType]}
          </Tag>
          {counts && <Tag color="cyan">{counts.totalDinersOrdered} comensales</Tag>}
        </Space>
        <span className="dash-count" style={{ fontSize: 16, color: 'var(--text-primary)' }}>
          {currencySymbol}{menu.price.toFixed(2)}
        </span>
      </div>

      {menu.description && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          {menu.description}
        </Text>
      )}

      <div
        style={{
          display: 'flex',
          gap: isMobile ? 12 : 24,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}
      >
        {sortedCourses.map(course =>
          renderCourseWithCounts(course, optionCounts, fixedCourseCounts, currencySymbol),
        )}
      </div>
    </div>
  );
};

/** Single source of truth for the gating, so the pill and the modal can't disagree. */
function useActiveDailyMenus() {
  const { data: menuSetting, isLoading: loadingSetting } = useGetBoolSettingQuery('MenuDelDiaActivo');
  const { data: activeMenus = [], isLoading: loadingMenus } = useGetActiveDailyMenusQuery(undefined, {
    skip: !menuSetting?.value,
  });

  return {
    enabled: !loadingSetting && !!menuSetting?.value,
    activeMenus,
    loadingMenus,
  };
}

export interface DailyMenuStatus {
  /** False when the MenuDelDiaActivo setting is off or still loading. */
  visible: boolean;
  /** True once at least one menu exists for today; false shows the "pendiente" pill. */
  configured: boolean;
  /** Diners across every active menu — the number the pill carries. */
  totalDiners: number;
}

/**
 * Drives the "Menú del día" pill. The diner total comes from the *range*
 * endpoint over the active menus' own business date rather than one counts
 * query per menu: one request instead of N, and the dashboard no longer pays
 * the N+1 the old widget did — the per-menu breakdown loads with the modal.
 */
export function useDailyMenuStatus(): DailyMenuStatus {
  const { enabled, activeMenus, loadingMenus } = useActiveDailyMenus();

  // Every active menu shares one business date (the backend filters on
  // `m.Date == businessDate`), so a single-day range covers all of them.
  const businessDate = activeMenus.length > 0 ? toBusinessDate(activeMenus[0].date) : '';
  const { data: rangeCounts } = useGetDailyMenuCountsRangeQuery(
    { startDate: businessDate, endDate: businessDate },
    { skip: !businessDate },
  );

  if (!enabled) {
    return { visible: false, configured: false, totalDiners: 0 };
  }

  return {
    visible: !loadingMenus,
    configured: activeMenus.length > 0,
    totalDiners: rangeCounts?.totalDinersOrdered ?? 0,
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const DailyMenuModal: React.FC<Props> = ({ open, onClose }) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { currencySymbol } = useCurrency();

  const { activeMenus, loadingMenus } = useActiveDailyMenus();

  const goToAdmin = () => {
    onClose();
    navigate('/daily-menus');
  };

  const isEmpty = !loadingMenus && activeMenus.length === 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={isMobile ? '100vw' : 720}
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      title={
        <Space>
          <CalendarOutlined />
          <Text strong>Menú del día</Text>
          {activeMenus.length > 1 && <Tag>{activeMenus.length} menús</Tag>}
        </Space>
      }
      footer={
        isEmpty ? (
          <Space>
            <Button onClick={onClose}>Ahora no</Button>
            <Button type="primary" onClick={goToAdmin}>Configurar menú del día</Button>
          </Space>
        ) : (
          <Button onClick={goToAdmin}>Gestionar menús</Button>
        )
      }
    >
      {loadingMenus ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
        </div>
      ) : isEmpty ? (
        <Alert
          type="warning"
          showIcon
          icon={<CalendarOutlined />}
          message="No se ha configurado el menú del día para hoy"
          description="Los comensales no podrán ordenarlo hasta que definas los tiempos y las opciones."
        />
      ) : (
        activeMenus.map((menu, index) => (
          <React.Fragment key={menu.id}>
            {index > 0 && <Divider style={{ margin: '12px 0' }} />}
            <MenuRow menu={menu} isMobile={isMobile} currencySymbol={currencySymbol} />
          </React.Fragment>
        ))
      )}
    </Modal>
  );
};

export default DailyMenuModal;
